/**
 * UserStatsCard Component
 *
 * Displays user authentication and activity metrics including:
 * - Total registered users
 * - Active sessions
 * - Xaman wallet connections
 * - New users and sessions in last 24 hours
 */

import React from 'react';
import type { UserAuthStats } from '../../types/admin';

interface UserStatsCardProps {
  userStats: UserAuthStats;
}

export function UserStatsCard({ userStats }: UserStatsCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">User Statistics</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
        {/* Total Users */}
        <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-blue-600 font-medium mb-1">Total Users</div>
          <div className="text-2xl sm:text-3xl font-bold text-blue-900">{userStats.totalUsers.toLocaleString()}</div>
        </div>

        {/* Active Sessions */}
        <div className="bg-green-50 rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-green-600 font-medium mb-1">Active Sessions</div>
          <div className="text-2xl sm:text-3xl font-bold text-green-900">{userStats.activeSessions.toLocaleString()}</div>
        </div>

        {/* Xaman Connections */}
        <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-purple-600 font-medium mb-1">Xaman Connections</div>
          <div className="text-2xl sm:text-3xl font-bold text-purple-900">{userStats.xamanConnections.toLocaleString()}</div>
        </div>
      </div>

      {/* Last 24 Hours Activity */}
      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
        <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-3">Last 24 Hours</h4>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500 mb-1">New Users</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">
              {userStats.last24Hours.newUsers.toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500 mb-1">New Sessions</div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">
              {userStats.last24Hours.newSessions.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
